import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, filter, map, of, merge, takeUntil, take } from 'rxjs';

import { ImportDataResultComponent } from '@app/pages/import-data-result/import-data-result.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { DataGridColumnDef, GetKpiConfigurationSearchByList, KpiConfigurationFieldName } from './kpi-configuration.constant';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { KpiConfiguration } from '@app/shared/classes/kpi-weight-management';
import { KpiConfigurationEditModalDialogComponent } from '@app/shared/components/edit-modal-dialog/kpi-configuration-edit-modal-dialog/kpi-configuration-edit-modal-dialog.component';
import { GetUserProfileMe } from '@app/shared/actions/common-data.action';
import { SiteConfigHelper } from '@app/shared/utils/site-config.helper';

@Component({
  selector: 'app-kpi-configuration',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, DataGridComponent, MessageDataComponent, CommonModule, TranslateModule,
    SmartSearchFormComponent, SpinnerButtonComponent, ImportDataResultComponent],
  templateUrl: './kpi-configuration.component.html',
  styleUrl: './kpi-configuration.component.css'
})
export class KpiConfigurationComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.KPI_CONFIGURATION_SEARCH_FORM;
  selectedItems: KpiConfiguration[] = [];
  pageFilterSetting: CommonPageFilterSetting = {
    functionName: NgxsActionName.GET_KPI_CONFIGURATION,
    sortBy: KpiConfigurationFieldName.kpiName,
    sortDirection: "asc",
    pageSize: 200,
    pageIndex: 0
  } as CommonPageFilterSetting;
  dataSource$: Observable<KpiConfiguration[]>;
  kpiConfigurationSearchByList$: Observable<SearchByItem[]> = of(GetKpiConfigurationSearchByList());

  @Select(AppDataState.currentKpiConfigurationSearchBy) currentKpiConfigurationSearchBy$: Observable<CommonSearchCriteria[]>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_KPI_CONFIGURATION)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_KPI_CONFIGURATION)) response$: Observable<WebApiResponse<KpiConfiguration[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_KPI_CONFIGURATION)) errorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_KPI_CONFIGURATION;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentKpiConfigurationSearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data.filter(kpiConfiguration =>
      kpiConfiguration.kpiCategory.kpiCategoryId !== SiteConfigHelper.GetKpiCategoryIdMapping().EconomyKPI)));

    // When refresh this page direct, the profile would be loading from this page. Then set profile from action
    this.actions$.pipe(ofActionSuccessful(GetUserProfileMe), takeUntil(this.destroy$))
      .subscribe(_ => this.store.dispatch(new AppDataAction.SetKpiConfigurationSearchValueOfCountry()));
    // When load site from other pages and open this page, profile is already loaded and we just set the profile directly
    this.store.dispatch(new AppDataAction.SetKpiConfigurationSearchValueOfCountry());
  }

  rowSelected = (data: KpiConfiguration[]) => {
    this.selectedItems = data ?? [];
  }

  override  onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiConfiguration());
  }

  onEdit = () => {
    this.dialog.open(KpiConfigurationEditModalDialogComponent, {
      width: '500px',
      data: { data: this.selectedItems[0], successHandler: this.onSearch },
      disableClose: true
    });
  }
}
